import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  Linkedin,
  Calendar,
  Edit2,
  Save,
  X,
  Trophy,
  Medal,
  Award,
  Users,
  Loader2,
  FileText,
  Clock,
  User,
  Plus,
  MessageSquare,
  Trash2,
  Send,
  Star,
  Sparkles,
  Smile,
  Frown,
  Meh,
  ThermometerSnowflake,
  AlertTriangle
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import { cn } from "../lib/utils";
import { EmailThreadsPanel } from "../components/EmailThreadsPanel";
import { LeadStudyLinks } from "../components/LeadStudyLinks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_STYLES = {
  Gold: { bg: "bg-yellow-100", text: "text-yellow-800", border: "border-yellow-200", icon: Trophy },
  Silver: { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-200", icon: Medal },
  Bronze: { bg: "bg-orange-100", text: "text-orange-800", border: "border-orange-200", icon: Award },
  Unranked: { bg: "bg-slate-50", text: "text-slate-500", border: "border-slate-100", icon: Users }
};

const SENTIMENTS = {
  Positive: { bg: "bg-green-100", text: "text-green-800", border: "border-green-200", icon: Smile },
  Neutral: { bg: "bg-slate-100", text: "text-slate-600", border: "border-slate-200", icon: Meh },
  Cautious: { bg: "bg-amber-100", text: "text-amber-800", border: "border-amber-200", icon: AlertTriangle },
  Cold: { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-200", icon: ThermometerSnowflake },
  Negative: { bg: "bg-red-100", text: "text-red-800", border: "border-red-200", icon: Frown },
};

const TierBadge = ({ tier, size = "default" }) => {
  const style = TIER_STYLES[tier] || TIER_STYLES.Unranked;
  const Icon = style.icon;
  return (
    <Badge className={cn(
      "tier-badge gap-1.5", 
      style.bg, 
      style.text, 
      "border", 
      style.border,
      size === "large" && "text-base px-4 py-2"
    )}>
      <Icon className={size === "large" ? "h-5 w-5" : "h-4 w-4"} />
      {tier} Tier
    </Badge>
  );
};

const CONTACT_REMARK_OPTIONS = [
  { value: "responded", label: "Responded", color: "green" },
  { value: "interested", label: "Interested", color: "green" },
  { value: "meeting_scheduled", label: "Meeting Scheduled", color: "blue" },
  { value: "active_opportunity", label: "Active Opportunity", color: "purple" },
  { value: "follow_up", label: "Pending Follow-up", color: "yellow" },
  { value: "not_interested", label: "Not Interested", color: "red" },
  { value: "no_response", label: "No Response", color: "gray" },
];

const REMARK_COLORS = {
  green: "bg-green-500",
  blue: "bg-blue-500",
  purple: "bg-purple-500",
  yellow: "bg-yellow-500",
  red: "bg-red-500",
  gray: "bg-gray-400"
};

const InfoRow = ({ icon: Icon, label, value, href, type = "text" }) => {
  if (!value) return null;
  
  const content = href ? (
    <a 
      href={href} 
      className="text-[#2E7AD1] hover:underline"
      target={type === "linkedin" ? "_blank" : undefined}
      rel={type === "linkedin" ? "noopener noreferrer" : undefined}
    >
      {value}
    </a>
  ) : (
    <span className="text-slate-900">{value}</span>
  );

  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="h-5 w-5 text-slate-400 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
        <p className="text-sm font-medium">{content}</p>
      </div>
    </div>
  );
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { 
      year: "numeric", 
      month: "short", 
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return dateStr;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    // Parse date as local time to avoid timezone offset issues
    // Dates in format "YYYY-MM-DD" should be treated as local dates
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString("en-US", { 
        year: "numeric", 
        month: "short", 
        day: "numeric" 
      });
    }
    // For ISO datetime strings with time component
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { 
      year: "numeric", 
      month: "short", 
      day: "numeric" 
    });
  } catch {
    return dateStr;
  }
};

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  
  // Notes state
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [feasibilities, setFeasibilities] = useState([]);
  const [analyzingSentiment, setAnalyzingSentiment] = useState(false);
  const [summarizingNote, setSummarizingNote] = useState(null);

  const user = JSON.parse(localStorage.getItem("stryde_user") || "{}");

  useEffect(() => {
    const fetchLead = async () => {
      try {
        const [leadRes, notesRes, feasRes] = await Promise.all([
          axios.get(`${API}/leads/${id}`),
          axios.get(`${API}/leads/${id}/notes`).catch(() => ({ data: [] })),
          axios.get(`${API}/feasibilities/by-contact/${id}`).catch(() => ({ data: [] }))
        ]);
        setLead(leadRes.data);
        setEditData(leadRes.data);
        setNotes(notesRes.data || []);
        setFeasibilities(feasRes.data || []);
      } catch (error) {
        console.error("Fetch lead error:", error);
        toast.error("Lead not found");
        navigate("/leads");
      } finally {
        setLoading(false);
      }
    };

    fetchLead();
  }, [id, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const prevStage = lead?.pipeline_stage;
      const response = await axios.put(`${API}/leads/${id}`, editData);
      setLead(response.data);
      setEditData(response.data);
      setEditing(false);
      toast.success("Lead updated successfully");
      // Notify if pipeline stage was auto-advanced
      if (response.data.pipeline_stage && prevStage && response.data.pipeline_stage !== prevStage) {
        toast.info(`Pipeline auto-advanced: ${prevStage} \u2192 ${response.data.pipeline_stage}`, { duration: 5000 });
      }
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to update lead");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditData(lead);
    setEditing(false);
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    setAddingNote(true);
    try {
      const response = await axios.post(`${API}/leads/${id}/notes`, {
        text: newNote,
        created_by: user.name || "User"
      });
      setNotes(prev => [response.data, ...prev]);
      setNewNote("");
      toast.success("Note added");
    } catch (error) {
      console.error("Add note error:", error);
      toast.error("Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/leads/${id}/notes/${noteId}`);
      setNotes(prev => prev.filter(n => n.id !== noteId));
      toast.success("Note deleted");
    } catch (error) {
      console.error("Delete note error:", error);
      toast.error("Failed to delete note");
    }
  };

  const handleAnalyzeSentiment = async () => {
    setAnalyzingSentiment(true);
    try {
      const res = await axios.post(`${API}/leads/${id}/ai-sentiment`);
      setLead(prev => ({
        ...prev,
        sentiment: res.data.sentiment,
        sentiment_reason: res.data.reason
      }));
      toast.success(`Sentiment: ${res.data.sentiment}`);
    } catch (error) {
      console.error("Sentiment analysis error:", error);
      toast.error("Failed to analyze sentiment");
    } finally {
      setAnalyzingSentiment(false);
    }
  };

  const handleSetSentiment = async (sentiment) => {
    try {
      await axios.put(`${API}/leads/${id}/sentiment`, { sentiment });
      setLead(prev => ({ ...prev, sentiment, sentiment_reason: "Manually set" }));
      toast.success(`Sentiment set to ${sentiment}`);
    } catch (error) {
      toast.error("Failed to set sentiment");
    }
  };

  const handleSummarizeNote = async (noteId) => {
    setSummarizingNote(noteId);
    try {
      const res = await axios.post(`${API}/leads/${id}/notes/${noteId}/summarize`);
      setNotes(prev => prev.map(n =>
        n.id === noteId ? { ...n, ai_summary: res.data.summary } : n
      ));
      toast.success("Summary generated");
    } catch (error) {
      console.error("Summarize error:", error);
      toast.error("Failed to generate summary");
    } finally {
      setSummarizingNote(null);
    }
  };

  const handleToggleStar = async () => {
    try {
      const response = await axios.post(`${API}/leads/${id}/star`);
      setLead(prev => ({ ...prev, is_starred: response.data.is_starred }));
      toast.success(response.data.is_starred ? "Contact starred" : "Star removed");
    } catch (error) {
      console.error("Toggle star error:", error);
      toast.error("Failed to update star");
    }
  };

  const getTherapeuticAreas = () => {
    if (!lead) return [];
    const areas = [];
    if (lead.dermatology > 0) areas.push("Dermatology");
    if (lead.rheumatology > 0) areas.push("Rheumatology");
    if (lead.neurology > 0) areas.push("Neurology");
    if (lead.ophthalmology > 0) areas.push("Ophthalmology");
    if (lead.immunology > 0) areas.push("Immunology");
    if (lead.pulmonary > 0) areas.push("Pulmonary");
    if (lead.cardiology > 0) areas.push("Cardiology");
    if (lead.metabolic > 0) areas.push("Metabolic");
    if (lead.gi > 0) areas.push("GI");
    if (lead.psych > 0) areas.push("Psychiatry");
    if (lead.onco > 0) areas.push("Oncology");
    if (lead.vaccine > 0) areas.push("Vaccine");
    if (lead.pain > 0) areas.push("Pain");
    return areas;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#2E7AD1]" />
      </div>
    );
  }

  if (!lead) return null;

  return (
    <div className="space-y-6" data-testid="lead-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/leads">
            <Button variant="ghost" size="sm" data-testid="back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Leads
            </Button>
          </Link>
        </div>
        <div className="flex items-center gap-2">
          {/* Star Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleToggleStar}
            className={cn(
              "p-2",
              lead.is_starred && "text-yellow-500"
            )}
            title={lead.is_starred ? "Remove star" : "Star this contact"}
            data-testid="star-btn"
          >
            <Star className={cn(
              "h-5 w-5",
              lead.is_starred && "fill-yellow-400"
            )} />
          </Button>
          
          {editing ? (
            <>
              <Button 
                variant="outline" 
                onClick={handleCancel}
                data-testid="cancel-edit-btn"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-[#2E7AD1] hover:bg-[#2563EB]"
                data-testid="save-btn"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Changes
              </Button>
            </>
          ) : (
            <Button 
              onClick={() => { setEditData({...lead}); setEditing(true); }}
              variant="outline"
              data-testid="edit-btn"
            >
              <Edit2 className="h-4 w-4 mr-2" />
              Edit Lead
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Company Card */}
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-slate-100 rounded-lg">
                    <Building2 className="h-8 w-8 text-slate-600" />
                  </div>
                  <div>
                    {editing ? (
                      <Input
                        value={editData.company || ""}
                        onChange={(e) => handleInputChange("company", e.target.value)}
                        className="text-xl font-bold mb-1"
                        placeholder="Company name"
                      />
                    ) : (
                      <CardTitle className="text-xl">
                        <Link
                          to={`/companies/${encodeURIComponent(lead.company)}`}
                          className="hover:text-[#2D6A4F] hover:underline underline-offset-2 transition-colors"
                          data-testid="lead-company-link"
                        >
                          {lead.company || "Unknown Company"}
                        </Link>
                      </CardTitle>
                    )}
                    <CardDescription className="flex items-center gap-2 mt-1">
                      {editing ? (
                        <Select
                          value={editData.company_tier}
                          onValueChange={(val) => handleInputChange("company_tier", val)}
                        >
                          <SelectTrigger className="w-[140px] h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Gold">Gold</SelectItem>
                            <SelectItem value="Silver">Silver</SelectItem>
                            <SelectItem value="Bronze">Bronze</SelectItem>
                            <SelectItem value="Unranked">Unranked</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <TierBadge tier={lead.company_tier} />
                      )}
                    </CardDescription>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Contact Person</Label>
                  {editing ? (
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={editData.first_name || ""}
                        onChange={(e) => handleInputChange("first_name", e.target.value)}
                        placeholder="First name"
                        className="flex-1"
                      />
                      <Input
                        value={editData.last_name || ""}
                        onChange={(e) => handleInputChange("last_name", e.target.value)}
                        placeholder="Last name"
                        className="flex-1"
                      />
                    </div>
                  ) : (
                    <p className="text-sm font-medium mt-1">
                      {lead.first_name || lead.contact || "—"} {lead.last_name || ""}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Role</Label>
                  {editing ? (
                    <Input
                      value={editData.role || ""}
                      onChange={(e) => handleInputChange("role", e.target.value)}
                      placeholder="Role/Title"
                      className="mt-1"
                    />
                  ) : (
                    <p className="text-sm font-medium mt-1">{lead.role || "—"}</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Role Type</Label>
                  {editing ? (
                    <Select
                      value={editData.role_type || "none"}
                      onValueChange={(v) => handleInputChange("role_type", v === "none" ? "" : v)}
                    >
                      <SelectTrigger className="mt-1" data-testid="role-type-select">
                        <SelectValue placeholder="Select role type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        <SelectItem value="CTM">CTM (Clinical Trial Manager)</SelectItem>
                        <SelectItem value="CRA">CRA (Clinical Research Associate)</SelectItem>
                        <SelectItem value="Coordinator">Coordinator</SelectItem>
                        <SelectItem value="MSL">MSL (Medical Science Liaison)</SelectItem>
                        <SelectItem value="PI">PI (Principal Investigator)</SelectItem>
                        <SelectItem value="BD/Commercial">BD / Commercial</SelectItem>
                        <SelectItem value="Medical Affairs">Medical Affairs</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm font-medium mt-1">
                      {lead.role_type ? (
                        <Badge variant="outline" className="text-xs" data-testid="role-type-badge">{lead.role_type}</Badge>
                      ) : "—"}
                    </p>
                  )}
                </div>
              </div>

              <Separator className="my-4" />

              <div className="space-y-2">
                {editing ? (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-slate-500">Email</Label>
                      <Input
                        value={editData.email || ""}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        placeholder="email@example.com"
                        type="email"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Phone</Label>
                      <Input
                        value={editData.phone || ""}
                        onChange={(e) => handleInputChange("phone", e.target.value)}
                        placeholder="+1 234 567 8900"
                        className="mt-1"
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <InfoRow 
                      icon={Mail} 
                      label="Email" 
                      value={lead.email}
                      href={lead.email ? `mailto:${lead.email}` : null}
                    />
                    <InfoRow icon={Phone} label="Phone" value={lead.phone} />
                    <InfoRow 
                      icon={Linkedin} 
                      label="LinkedIn" 
                      value={lead.linkedin_url ? "View Profile" : null}
                      href={lead.linkedin_url}
                      type="linkedin"
                    />
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Status & Engagement</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs text-slate-500">Current Status</Label>
                {editing ? (
                  <Select
                    value={editData.status || ""}
                    onValueChange={(val) => handleInputChange("status", val)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="No Response">No Response</SelectItem>
                      <SelectItem value="Contacted">Contacted</SelectItem>
                      <SelectItem value="Responded">Responded</SelectItem>
                      <SelectItem value="Responded: No Needs Currently">No Needs Currently</SelectItem>
                      <SelectItem value="Follow Up">Follow Up</SelectItem>
                      <SelectItem value="Feasibility Received">Feasibility Received</SelectItem>
                      <SelectItem value="Feasibility Completed">Feasibility Completed</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge 
                    variant="outline" 
                    className="mt-1 bg-blue-50 text-blue-700 border-blue-200"
                  >
                    {lead.status || "No Status"}
                  </Badge>
                )}
              </div>
              
              {/* Contact Remark */}
              <div>
                <Label className="text-xs text-slate-500">Contact Remark</Label>
                {editing ? (
                  <div className="flex gap-2 mt-1">
                    <Select
                      value={editData.contact_remark || ""}
                      onValueChange={(val) => {
                        const option = CONTACT_REMARK_OPTIONS.find(o => o.value === val);
                        handleInputChange("contact_remark", option?.label || "");
                        handleInputChange("contact_remark_color", option?.color || "gray");
                      }}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select remark" />
                      </SelectTrigger>
                      <SelectContent>
                        {CONTACT_REMARK_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            <div className="flex items-center gap-2">
                              <span className={cn("w-2 h-2 rounded-full", REMARK_COLORS[option.color])} />
                              {option.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mt-1">
                    {lead.contact_remark_color && (
                      <span className={cn("w-3 h-3 rounded-full", REMARK_COLORS[lead.contact_remark_color] || "bg-gray-400")} />
                    )}
                    <span className="text-sm font-medium">
                      {lead.contact_remark || "Not set"}
                    </span>
                  </div>
                )}
                <p className="text-xs text-slate-400 mt-1">
                  Mark contacts based on their engagement level
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Sentiment Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Smile className="h-5 w-5 text-slate-400" />
                Lead Sentiment
              </CardTitle>
              <CardDescription>Track how this lead feels about your outreach</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 flex-wrap">
                {lead.sentiment ? (() => {
                  const s = SENTIMENTS[lead.sentiment];
                  const Icon = s?.icon || Meh;
                  return (
                    <div className={cn("flex items-center gap-2 px-3 py-1.5 rounded-full border", s?.bg, s?.text, s?.border)}>
                      <Icon className="h-4 w-4" />
                      <span className="font-medium text-sm">{lead.sentiment}</span>
                    </div>
                  );
                })() : (
                  <span className="text-sm text-slate-400">Not analyzed yet</span>
                )}
                {lead.sentiment_reason && (
                  <span className="text-xs text-slate-500 italic">{lead.sentiment_reason}</span>
                )}
              </div>

              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleAnalyzeSentiment}
                  disabled={analyzingSentiment}
                  data-testid="ai-sentiment-btn"
                  className="text-xs"
                >
                  {analyzingSentiment ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3 mr-1" />
                  )}
                  AI Analyze
                </Button>
                <span className="text-xs text-slate-400">or set manually:</span>
                {Object.entries(SENTIMENTS).map(([key, s]) => {
                  const Icon = s.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => handleSetSentiment(key)}
                      className={cn(
                        "p-1.5 rounded-full border transition-all hover:scale-110",
                        lead.sentiment === key ? cn(s.bg, s.border, "ring-2 ring-offset-1") : "border-slate-200 hover:border-slate-300"
                      )}
                      title={key}
                      data-testid={`sentiment-${key.toLowerCase()}`}
                    >
                      <Icon className={cn("h-4 w-4", lead.sentiment === key ? s.text : "text-slate-400")} />
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Email Conversations Card */}
          <EmailThreadsPanel
            leadId={lead?.id}
            title="Email Conversations"
            description="All inbound and outbound emails with this contact (and their email address)"
          />

          {/* Linked Studies */}
          <LeadStudyLinks leadId={lead?.id} companyName={lead?.company} />

          {/* Notes History Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-slate-400" />
                Notes History
              </CardTitle>
              <CardDescription>Add timestamped notes for this lead</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Add Note Form */}
              <div className="flex gap-2 mb-4">
                <Textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note..."
                  rows={2}
                  className="flex-1"
                />
                <Button 
                  onClick={handleAddNote}
                  disabled={addingNote || !newNote.trim()}
                  className="bg-[#2E7AD1] hover:bg-[#2563EB]"
                >
                  {addingNote ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {/* Notes List */}
              <ScrollArea className="h-[300px]">
                {notes.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <MessageSquare className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>No notes yet</p>
                    <p className="text-xs">Add your first note above</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {notes.map((note) => (
                      <div 
                        key={note.id}
                        className="p-3 bg-slate-50 rounded-lg border border-slate-100"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-700 whitespace-pre-wrap">
                              {note.text}
                            </p>
                            {/* AI Summary */}
                            {note.ai_summary && (
                              <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-100">
                                <div className="flex items-center gap-1 mb-1">
                                  <Sparkles className="h-3 w-3 text-blue-500" />
                                  <span className="text-xs font-medium text-blue-600">AI Summary</span>
                                </div>
                                <p className="text-xs text-blue-800 leading-relaxed">{note.ai_summary}</p>
                              </div>
                            )}
                            <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                              <User className="h-3 w-3" />
                              <span>{note.created_by}</span>
                              <span>-</span>
                              <Clock className="h-3 w-3" />
                              <span>{formatDateTime(note.created_at)}</span>
                            </div>
                          </div>
                          <div className="flex flex-col gap-1">
                            {!note.ai_summary && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => handleSummarizeNote(note.id)}
                                disabled={summarizingNote === note.id}
                                data-testid={`summarize-note-${note.id}`}
                                title="Generate AI summary"
                              >
                                {summarizingNote === note.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Sparkles className="h-3 w-3" />
                                )}
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                              onClick={() => handleDeleteNote(note.id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>

              {/* Legacy Notes */}
              {lead.notes && (
                <div className="mt-4 pt-4 border-t">
                  <Label className="text-xs text-slate-500">Legacy Notes</Label>
                  <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap bg-slate-50 p-3 rounded-lg">
                    {lead.notes}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Linked Feasibilities */}
          {feasibilities.length > 0 && (
            <Card data-testid="lead-feasibilities">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5 text-purple-400" />
                  Feasibilities ({feasibilities.length})
                </CardTitle>
                <CardDescription>Feasibility submissions linked to this contact</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {feasibilities.map(f => (
                    <div key={f.id} className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{f.sponsor || "—"} — {f.study_name || f.protocol_number || "—"}</p>
                          <p className="text-xs text-slate-500">{f.indication || "—"} · {f.phase || "—"}</p>
                        </div>
                        <Badge className={cn(
                          "text-xs border-0",
                          f.decision_outcome === "Selected" ? "bg-green-100 text-green-700" :
                          f.decision_outcome === "Not Selected" ? "bg-red-100 text-red-700" :
                          "bg-amber-100 text-amber-700"
                        )}>
                          {f.decision_outcome || "Pending"}
                        </Badge>
                      </div>
                      {(f.win_reason || f.loss_reason) && (
                        <p className="text-xs text-slate-400 mt-1">Reason: {f.win_reason || f.loss_reason}</p>
                      )}
                      <p className="text-xs text-slate-400 mt-1">Created: {formatDate(f.created_at)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Timeline & Details */}
        <div className="space-y-6">
          {/* Therapeutic Areas */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Therapeutic Areas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {getTherapeuticAreas().length > 0 ? (
                  getTherapeuticAreas().map((ta) => (
                    <Badge 
                      key={ta} 
                      className="bg-[#2E7AD1]/10 text-[#2E7AD1] border border-[#2E7AD1]/20"
                    >
                      {ta}
                    </Badge>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">No therapeutic areas assigned</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Follow-up Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-slate-400" />
                Follow-up Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b">
                  <span className="text-sm text-slate-600">First Email Sent</span>
                  <span className="text-sm font-medium text-slate-400">
                    {formatDate(lead.date_first_email_sent) || "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b bg-blue-50 -mx-4 px-4 rounded">
                  <span className="text-sm text-blue-700 font-medium">Latest Email Sent</span>
                  {editing ? (
                    <Input
                      type="date"
                      value={editData.date_latest_email_sent || ""}
                      onChange={(e) => handleInputChange("date_latest_email_sent", e.target.value)}
                      className="w-40 text-sm"
                    />
                  ) : (
                    <span className="text-sm font-medium text-blue-700">
                      {formatDate(lead.date_latest_email_sent) || "—"}
                    </span>
                  )}
                </div>
                {[1, 2, 3, 4].map((num) => {
                  const dateField = `follow_up_${num}_date`;
                  const date = editing ? editData[dateField] : lead[dateField];
                  const isPastDue = date && (() => {
                    if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
                      const [y, m, d] = date.split('-').map(Number);
                      return new Date(y, m - 1, d) < new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
                    }
                    return new Date(date) < new Date();
                  })();
                  return (
                    <div key={num} className="flex items-center justify-between py-2 border-b last:border-0">
                      <span className="text-sm text-slate-600">Follow-up {num}</span>
                      {editing ? (
                        <div className="flex items-center gap-2">
                          <Input
                            type="date"
                            value={date || ""}
                            onChange={(e) => handleInputChange(dateField, e.target.value)}
                            className="w-40 text-sm"
                          />
                          {date && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                              onClick={() => handleInputChange(dateField, "")}
                              title="Clear date"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ) : (
                        <span className={cn(
                          "text-sm font-medium",
                          isPastDue && "text-orange-600"
                        )}>
                          {formatDate(date) || "—"}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              {editing && (
                <p className="text-xs text-slate-400 mt-3">
                  Tip: Update "Latest Email Sent" to auto-recalculate all follow-up dates. Changing Follow-up 1 will cascade to FU 2-4. Use the X to clear individual follow-ups.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Feasibility Status */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-slate-400" />
                Feasibility
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b">
                  <span className="text-sm text-slate-600">Received</span>
                  {editing ? (
                    <Input
                      type="date"
                      value={editData.date_received_feasibility || ""}
                      onChange={(e) => handleInputChange("date_received_feasibility", e.target.value)}
                      className="w-40 text-sm"
                    />
                  ) : (
                    <span className={cn(
                      "text-sm font-medium",
                      lead.date_received_feasibility && "text-green-600"
                    )}>
                      {formatDate(lead.date_received_feasibility) || "—"}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-slate-600">Completed</span>
                  {editing ? (
                    <Input
                      type="date"
                      value={editData.date_feasibility_completed || ""}
                      onChange={(e) => handleInputChange("date_feasibility_completed", e.target.value)}
                      className="w-40 text-sm"
                    />
                  ) : (
                    <span className={cn(
                      "text-sm font-medium",
                      lead.date_feasibility_completed && "text-green-600"
                    )}>
                      {formatDate(lead.date_feasibility_completed) || "—"}
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Owner */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5 text-slate-400" />
                BD Owner
              </CardTitle>
            </CardHeader>
            <CardContent>
              {editing ? (
                <Select
                  value={editData.bd_person_owner || ""}
                  onValueChange={(val) => handleInputChange("bd_person_owner", val)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Assign owner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Chintan">Chintan</SelectItem>
                    <SelectItem value="Vaibhavi">Vaibhavi</SelectItem>
                    <SelectItem value="Ed">Ed</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm font-medium">
                  {lead.bd_person_owner || "Unassigned"}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
